import React from 'react';

// Shallow array equality, treating two missing arrays as equal.
const sameKeys = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((v, i) => Object.is(v, b[i]));
};

// Reusable error boundary. Must be a class — React only exposes lifecycle error
// hooks on classes. `fallback` is a node or a render-fn (error) => node.
// `resetKeys` is an optional array; when any of its entries changes (e.g. a fresh
// fetch result), a latched error is cleared so the children get another render —
// the section recovers without needing a remount/tab switch.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && !sameKeys(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') return fallback(error);
      if (fallback !== undefined && fallback !== null) return fallback;
      return null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
